import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import { Button } from 'ui-components/components/button';
import { UncontrolledCollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section/uncontrolled';

import Global from '../../store/global-selectors';
import { setupCsw } from '../../store/scan-to-print/scan-to-print.actions';
import { printTheLabel } from '../../store/scan-to-print/s2p-purchase-and-print.actions';

import PrintPackingSlipHotkey from '../../components/hotkeys/advanced-hotkeys/print-packing-slip';
import Cover from '../../components/cover';
import ReprintLabelHotkeyComponent from '../../components/hotkeys/reprint-label';
import ScanToPrintHotkeyComponent from '../../components/hotkeys/scan-to-print';
import SellerPresetsHotkey from '../../components/hotkeys/seller-presets';
import { ShipmentObjectContainer } from '../../containers/shipment-object-container';
import { FulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { EntitiesAdapterForFulfillmentPlannerContext } from '../../fulfillment-planner';
import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import { messages } from '../messages';
import { FulfillmentPlanner } from './fulfillment-planner';
import style from './style.module.less';
import SingleOutboundShipmentDisplayContainer from '../../fulfillment-plans/shipments-display/single-outbound-shipment-display/connected-order-context';
import FulfillmentPlannerContextDrivenOtherShippingOptions from '../../fulfillment-planner/other-shipping-options';
import { availableModes } from '../constants';
import { ScanToVerifyReader } from '../../store/scan-to-print/scan-to-verify-reader';
import { getDefaultMode } from '../../store/scan-to-print/scan-to-print-reader';
import { usePackingSlipDisabledCheck } from '../../components/orders-grid/print-document-button/hooks/use-packing-slip';

const isCSWDisabled = (mode, isVerified, s2pStatus, isHeaderFocused, isSearching) => {
  if (mode !== availableModes.scanToPrint && !isVerified) {
    return true;
  }
  return s2pStatus === 'processing' || s2pStatus === 'printing' || isHeaderFocused || isSearching;
};
const selectPresetFulfillmentPlanId = createSelector(
  (fulfillmentPlanId) => fulfillmentPlanId,
  (fulfillmentPlanId) => (fulfillmentPlanId ? [fulfillmentPlanId] : emptyArray),
);

export const ScanToPrintFulfillmentPlanner = React.memo(() => {
  const dispatch = useDispatch();
  const fulfillmentPlanId = useSelector(Global.selectScanToPrintFulfillmentPlanId);
  const isVerified = useSelector((state) =>
    new ScanToVerifyReader({ state }).isFulfillmentPlanVerified({
      fulfillmentPlanId,
    }),
  );
  const isHeaderFocused = useSelector(Global.selectScanToPrintIsHeaderFocused);
  const mode = useSelector(Global.selectScanToPrintMode) || getDefaultMode();
  const isSearching = useSelector((state) => Global.selectScanToPrintOrderSearchResult(state)?.isSearching);
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || emptyObject;
  const { fulfillmentIds = emptyArray } = fulfillmentPlan;
  const salesOrders =
    useSelector((state) =>
      Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
        fulfillmentIds,
      }),
    ) || emptyArray;
  const fulfillments = useSelector((state) => Global.selectEntitiesFulfillmentsByIds(state, { fulfillmentIds }));
  const statusIndicator = useSelector(Global.selectScanToPrintStatusIndicator);
  const { status } = statusIndicator;
  const isDisabled = isCSWDisabled(mode, isVerified, status, isHeaderFocused, isSearching);

  useEffect(() => {
    dispatch(setupCsw(fulfillmentPlanId));
  }, [dispatch, fulfillmentPlanId]);

  const hasNonVoidedFulfillments = fulfillments.some((fulfillment) => fulfillment.isLabelVoided === false);
  const PrintLabelHotkeyComponent = hasNonVoidedFulfillments ? ReprintLabelHotkeyComponent : ScanToPrintHotkeyComponent;

  const showShipments = fulfillments.length && fulfillments.every((f) => !f.isLabelVoided);
  const wrapperClasses = classnames(
    style['csw-wrapper'],
    { [style.disabled]: isDisabled },
    { [style['csw-wrapper-reprint']]: showShipments },
  );

  const onReprint = useCallback(() => dispatch(printTheLabel()), [dispatch]);

  const { isDisabled: packingSlipIsDisabled, onClick: packingSlipHotkeyHandler } = usePackingSlipDisabledCheck({
    fulfillmentPlans: [fulfillmentPlan],
    salesOrders,
  });

  return (
    <div className={wrapperClasses}>
      <Cover isDisabled={isDisabled} isRight>
        <EntitiesAdapterForFulfillmentPlannerContext
          key={fulfillmentPlanId}
          fulfillmentPlanId={fulfillmentPlanId}
          isReadOnly={showShipments}
        >
          <FulfillmentPlannerContext.Consumer>
            {({ applyPresetConfiguration }) => (
              <SellerPresetsHotkey
                fulfillmentPlanIds={selectPresetFulfillmentPlanId(fulfillmentPlanId)}
                onApplyPreset={applyPresetConfiguration}
              >
                <PrintLabelHotkeyComponent isDisabled={isDisabled}>
                  {!packingSlipIsDisabled && <PrintPackingSlipHotkey handler={packingSlipHotkeyHandler} />}
                  {showShipments ? (
                    <div className={style['scan-shipment-object']}>
                      <ShipmentObjectContainer
                        className={style['shipment-object']}
                        fulfillments={fulfillments}
                        shipment={fulfillmentPlan}
                      />
                      <UncontrolledCollapsibleListSection
                        alwaysOpen
                        header={<FormattedMessage {...messages.shipmentSectionHeader} />}
                        theme='white'
                      >
                        <div className={style['shipment-display-wrapper']}>
                          <SingleOutboundShipmentDisplayContainer fulfillmentPlanId={fulfillmentPlanId} />
                        </div>
                      </UncontrolledCollapsibleListSection>
                      <FulfillmentPlannerContextDrivenOtherShippingOptions headerClassName={style['shipment-header-expander']} isReadOnly />
                      <Button className={style['reprint-button']} disabled={isDisabled} onClick={onReprint} theme='primary'>
                        <FormattedMessage {...messages.reprintLabelButton} />
                      </Button>
                    </div>
                  ) : (
                    <FulfillmentPlanner fulfillmentPlanId={fulfillmentPlanId} />
                  )}
                </PrintLabelHotkeyComponent>
              </SellerPresetsHotkey>
            )}
          </FulfillmentPlannerContext.Consumer>
        </EntitiesAdapterForFulfillmentPlannerContext>
      </Cover>
    </div>
  );
});
